import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import subscribe from "@insite/client-framework/Store/CommonHandlers/Subscribe";
import { getLink, getPageLinkByNodeId, useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import ChevronRight from "@insite/mobius/Icons/ChevronRight";
import LazyImage from "@insite/mobius/LazyImage";
import MobiusLink from "@insite/mobius/Link";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, History, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, useContext, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

export interface InfoLinkFieldValue {
    type: "InfoLink";
    value: string;
}

interface InfoCardModel {
    fields: {
        image: string;
        title: string;
        description: string;
        link: InfoLinkFieldValue;
        linkTitle: string;
        bannerType: string;
        linkType: string;
        uploadDocument: string;
    };
}

const enum fields {
    image = "image",
    title = "title",
    description = "description",
    link = "link",
    linkTitle = "linkTitle",
    reverse = "reverse",
    bannerType = "bannerType",
    linkType = "linkType",
    placeholder = "placeholder",
    bannernewtab = "bannernewtab",
    uploadDocument = "uploadDocument",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.image]: string;
        [fields.title]: string;
        [fields.description]: string;
        [fields.bannerType]: string;
        [fields.linkType]: string;
        [fields.placeholder]: string;
        [fields.link]: InfoLinkFieldValue;
        [fields.linkTitle]: string;
        [fields.reverse]: boolean;
        [fields.bannernewtab]: boolean;
        [fields.uploadDocument]: string;
    };
}
export interface MappedLink {
    title: string;
    url: string;
}

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => {
    const {
        fields: { link, linkTitle },
    } = ownProps;
    let mappedInfoLink: MappedLink | undefined;
    const { value } = link;
    const readonlyPageLink = getPageLinkByNodeId(state, value);
    if (readonlyPageLink) {
        mappedInfoLink = { ...readonlyPageLink };
        if (linkTitle) {
            mappedInfoLink.title = linkTitle;
        }
    }
    return {
        mappedInfoLink,
        state,
    };
};
const mapDispatchToProps = {
    subscribe,
};

type Props = HasHistory & OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface IInfoCardsStyles {
    customflyImgWrapper?: InjectableCss;
    customDescriptionText?: TypographyPresentationProps;
    customFlyTitle?: TypographyPresentationProps;
    bannertextleft?: InjectableCss;
    bannertextright?: InjectableCss;
    bannercontainernormal?: InjectableCss;
    bannercontainerrevers?: InjectableCss;
    emailGridItem?: GridItemProps;
    emailGridIteminput?: GridItemProps;
    buttonGrid?: GridItemProps;
    emailTextField?: TextFieldPresentationProps;
    emailButton?: ButtonPresentationProps;
    bannerButton?: ButtonPresentationProps;
    downloadLink?: LinkPresentationProps;
}

export const infoCardsStyles: IInfoCardsStyles = {
    customflyImgWrapper: {
        css: css`
            box-sizing: border-box;
            width: 100%;
            line-height: 0;
            div[class^="LazyImageStyle"] {
                width: 100%;
                img {
                    height: 193px;
                    width: 100%;
                    object-fit: cover;
                }
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        null,
                        css`
                            height: 100%;
                            div[class^="LazyImageStyle"] {
                                img {
                                    height: -webkit-fill-available;
                                }
                            }
                        `,
                        css`
                            height: 100%;
                            div[class^="LazyImageStyle"] {
                                img {
                                    height: -webkit-fill-available;
                                }
                            }
                        `,
                    ],
                    "min",
                )}
        `,
    },
    customFlyTitle: {
        variant: "p",
        css: css`
            font-size: 30px;
            font-weight: 600;
            letter-spacing: 0;
            line-height: 36px;
            color: #fff;
            margin-bottom: 7px !important;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            font-size: 21px;
                            font-weight: 600;
                            letter-spacing: 0;
                            line-height: 25px;
                            margin-bottom: 8px !important;
                        `,
                        css`
                            font-size: 21px;
                            font-weight: 600;
                            letter-spacing: 0;
                            line-height: 25px;
                            margin-bottom: 8px !important;
                        `,
                        css`
                            font-size: 30px;
                            font-weight: 600;
                            letter-spacing: 0;
                            line-height: 36px;
                            margin-bottom: 7px !important;
                        `,
                        css`
                            font-size: 30px;
                            font-weight: 600;
                            letter-spacing: 0;
                            line-height: 36px;
                            margin-bottom: 7px !important;
                        `,
                        css`
                            font-size: 30px;
                            font-weight: 600;
                            letter-spacing: 0;
                            line-height: 36px;
                            margin-bottom: 7px !important;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    bannertextright: {
        css: css`
            width: 53%;
            display: flex;
            flex-direction: column;
            justify-content: center;
            background-color: #006bc0;
            padding: 0 30px;
            position: relative;
            z-index: 1;
            :after {
                content: " ";
                position: absolute;
                display: block;
                width: 100%;
                height: 100%;
                top: 0;
                left: 0;
                z-index: -1;
                background: #006bc0;
                transform-origin: bottom left;
                -ms-transform: skew(30deg, 0deg);
                -webkit-transform: skew(30deg, 0deg);
                transform: skew(30deg, 0deg);
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            width: 100%;
                            padding-top: 25px;
                            padding-bottom: 29px;
                            padding-left: 20px;
                            padding-right: 20px;
                            :after {
                                display: none;
                            }
                        `,
                        css`
                            width: 100%;
                            padding-top: 25px;
                            padding-bottom: 29px;
                            padding-left: 20px;
                            padding-right: 20px;
                            :after {
                                display: none;
                            }
                        `,
                        css`
                            width: 100%;
                            padding-top: 25px;
                            padding-bottom: 29px;
                            padding-left: 20px;
                            padding-right: 20px;
                            :after {
                                display: none;
                            }
                        `,
                        css`
                            width: 53%;
                            padding-top: 0px;
                            padding-bottom: 0px;

                            :after {
                                content: " ";
                                position: absolute;
                                display: block;
                                width: 100%;
                                height: 100%;
                                top: 0;
                                left: -112px;
                                z-index: -1;
                                background: #006bc0;
                                transform-origin: bottom left;
                                -ms-transform: skew(-30deg, 0deg);
                                -webkit-transform: skew(-30deg, 0deg);
                                transform: skew(-30deg, 0deg);
                            }
                        `,
                        css`
                            width: 53%;
                            padding-top: 0px;
                            padding-bottom: 0px;

                            :after {
                                content: " ";
                                position: absolute;
                                display: block;
                                width: 100%;
                                height: 100%;
                                top: 0;
                                left: -112px;
                                z-index: -1;
                                background: #006bc0;
                                transform-origin: bottom left;
                                -ms-transform: skew(-30deg, 0deg);
                                -webkit-transform: skew(-30deg, 0deg);
                                transform: skew(-30deg, 0deg);
                            }
                        `,
                    ],
                    "min",
                )}
        `,
    },
    bannertextleft: {
        css: css`
            width: 53%;
            display: flex;
            flex-direction: column;
            justify-content: center;
            background-color: #006bc0;
            padding-left: 41px;
            position: relative;
            z-index: 1;

            padding-bottom: 10px;

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            width: 100%;
                            padding-top: 25px;
                            padding-bottom: 29px;
                            padding-left: 20px;
                            padding-right: 20px;
                            :after {
                                display: none;
                            }
                        `,
                        css`
                            width: 100%;
                            padding-top: 25px;
                            padding-bottom: 29px;
                            padding-left: 20px;
                            padding-right: 20px;
                            :after {
                                display: none;
                            }
                        `,
                        css`
                            width: 100%;
                            padding-top: 25px;
                            padding-bottom: 29px;
                            padding-left: 20px;
                            padding-right: 20px;
                            :after {
                                display: none;
                            }
                        `,
                        css`
                            width: 53%;
                            padding-top: 0px;
                            padding-bottom: 0px;
                            padding-left: 41px;
                            :after {
                                content: " ";
                                position: absolute;
                                display: block;
                                width: 101.5%;
                                height: 100%;
                                top: 0;
                                left: 0;
                                z-index: -1;
                                background: #006bc0;
                                transform-origin: bottom left;
                                -ms-transform: skew(-30deg, 0deg);
                                -webkit-transform: skew(-30deg, 0deg);
                                transform: skew(-30deg, 0deg);
                            }
                        `,
                        css`
                            width: 53%;
                            padding-top: 0px;
                            padding-bottom: 0px;
                            padding-left: 41px;
                            :after {
                                content: " ";
                                position: absolute;
                                display: block;
                                width: 101.5%;
                                height: 100%;
                                top: 0;
                                left: 0;
                                z-index: -1;
                                background: #006bc0;
                                transform-origin: bottom left;
                                -ms-transform: skew(-30deg, 0deg);
                                -webkit-transform: skew(-30deg, 0deg);
                                transform: skew(-30deg, 0deg);
                            }
                        `,
                    ],
                    "min",
                )}
        `,
    },
    bannercontainernormal: {
        css: css`
            display: flex;
            height: 193px;
            max-width: 1140px;
            margin: 0 auto;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            height: auto;
                            flex-direction: column-reverse;
                        `,
                        css`
                            height: auto;
                            flex-direction: column-reverse;
                        `,
                        css`
                            height: auto;
                            flex-direction: column-reverse;
                        `,
                        css`
                            height: 193px;
                            flex-direction: row;
                        `,
                        css`
                            height: 193px;
                            flex-direction: row;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    bannercontainerrevers: {
        css: css`
            display: flex;
            height: 193px;
            max-width: 1140px;
            margin: 0 auto;
            flex-direction: row-reverse;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            height: auto;

                            flex-direction: column-reverse;
                        `,
                        css`
                            height: auto;

                            flex-direction: column-reverse;
                        `,
                        css`
                            height: auto;

                            flex-direction: column-reverse;
                        `,
                        css`
                            height: 193px;

                            flex-direction: row-reverse;
                        `,
                        css`
                            height: 193px;

                            flex-direction: row-reverse;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    customDescriptionText: {
        variant: "p",
        css: css`
            font-size: 16px;
            font-weight: 300;
            letter-spacing: 0;
            line-height: 22px;
            color: #fff;
            margin-bottom: 16px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            margin-bottom: 22px;
                        `,
                        css`
                            margin-bottom: 22px;
                        `,
                        css`
                            margin-bottom: 16px;
                        `,
                        css`
                            margin-bottom: 16px;
                        `,
                        css`
                            margin-bottom: 16px;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    emailGridItem: {
        width: 12,
        css: css`
            padding: 0px;

            @media (max-width: 767px) {
                flex-direction: column;
            }
        `,
    },
    emailGridIteminput: {
        width: [12, 12, 7, 7, 7],
        css: css`
            padding: 0;
            @media (max-width: 767px) {
                width: 100%;
                padding-right: 20px;
                padding-bottom: 10px;
            }
            @media (min-width: 768px) and (max-width: 991px) {
                padding-right: 10px;
            }
        `,
    },
    buttonGrid: {
        width: [12, 12, 5, 5, 5],
        css: css`
            padding: 0px;
            @media (max-width: 767px) {
                display: flex;
                width: 100%;
            }
        `,
    },
    emailTextField: {
        cssOverrides: {
            formField: css`
                width: 100%;

                @media (max-width: 767px) {
                    margin-bottom: 18px;
                }
                @media (min-width: 768px) and (max-width: 991px) {
                    margin: 0px;
                }

                div[class^="DescriptionWrapper"] {
                    margin-top: 6px !important;
                    margin-left: 10px;
                    @media (min-width: 992px) and (max-width: 1200px) {
                        margin-top: 4px !important;
                    }
                    position: absolute;
                    > span {
                        color: #760303;
                        font-size: 13px;
                        letter-spacing: 0;
                        line-height: 16px;
                    }
                }
            `,
            inputSelect: css`
                background-color: rgb(245, 245, 245, 0.2);
                border: 1px solid #ffffff !important;
                &:focus {
                    border: 1px solid #ffffff !important;
                }
                ::placeholder {
                    font-size: 16px;
                    font-weight: 300;
                    letter-spacing: 0;
                    line-height: 24px;
                    color: #fff;
                    text-transform: uppercase;
                }
                margin-top: 15px;
                @media (min-width: 992px) and (max-width: 1200px) {
                    margin-top: 6px;
                }
                @media (max-width: 767px) {
                    margin-top: 0px;

                    ::placeholder {
                        font-size: 16px;
                        font-weight: 300;
                        letter-spacing: 0;
                        line-height: 24px;
                        color: #fff;
                    }
                }
            `,
        },
    },
    bannerButton: {
        css: css`
        height: 40px;
        width: 166px;
        padding: 0 0px;
        background-color: #ffffff !important;
        align-items: "center";                            
        text-transform: uppercase;
        color: #33333a;
        text-transform: uppercase;
        border: none;
        justify-content: center;
        @media (max-width: 767px) {
            width: 100%;
        }
        &:hover {
            background-color: #fff;
        }
        span {
            font-family: "proxima-nova";
        font-size: 12px;
        font-weight: 600;
        letter-spacing: 2px;
        line-height: normal;
        text-align: center;
            color: #33333a;
            padding: 10px 15px;
            text-transform: uppercase;
            &:hover {
                color: #006bc0;
            }
            
    `,
    },
    downloadLink: {
        css: css`
        height: 40px;
        width: 166px;
        padding: 0 0px;
        background-color: #ffffff !important;
        align-items: "center";                            
        text-transform: uppercase;
        color: #33333a;
        text-transform: uppercase;
        border: none;
        justify-content: center;
        @media (max-width: 767px) {
            width: 100%;
        }
        &:hover {
            background-color: #fff;
        }
        span {
            font-family: "proxima-nova";
        font-size: 12px;
        font-weight: 600;
        letter-spacing: 2px;
        line-height: normal;
        text-align: center;
            color: #33333a;
            padding: 10px 15px;
            text-transform: uppercase;
            &:hover {
                color: #006bc0;
            }
            
    `,
    },
    emailButton: {
        css: css`
        height: 40px;
        width: 166px;
        padding: 0 0px;
        margin-left:20px;
        background-color: #ffffff !important;
        align-items: "center";                            
        text-transform: uppercase;
        color: #33333a;
        text-transform: uppercase;
        border: none;
        margin-top: 15px;
        @media (min-width: 992px) and (max-width: 1200px) {
            margin-top: 6px;
        }
        @media (max-width: 767px) {
            width: auto;
            margin-top: 0px;
            margin-left: 0;
        }
        &:hover {
            background-color: #fff;
            span {
                color: #006bc0 !important;
            }
        }
        span {
            font-family: "proxima-nova";
        font-size: 12px;
        font-weight: 600;
        letter-spacing: 2px;
        line-height: normal;
        text-align: center;
            color: #33333a;
            padding: 10px 15px;
            text-transform: uppercase;
            &:hover {
                color: #006bc0;
            }
            
    `,
    },
};

const styles = infoCardsStyles;
const emailRegexp = new RegExp("\\w+([-+.']\\w+)*@\\w+([-.]\\w+)*\\.\\w+([-.]\\w+)*");

const INTbanner: React.FC<Props> = ({ fields, mappedInfoLink, history, subscribe }) => {
    const emailRequiredFieldMessage = siteMessage("EmailSubscription_EmailIsRequiredErrorMessage");
    const emailFieldMessage = siteMessage("EmailSubscription_EmailIsInvalidErrorMessage");
    const [email, setEmail] = useState("");
    const [emailError, setEmailError] = useState(emailRequiredFieldMessage);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const toasterContext = useContext(ToasterContext);
    const onClick = (history: History) => {
        setIsSubmitted(true);

        if (mappedInfoLink) {
            if (fields.bannerType === "news") {
                if (!validateEmail(email)) {
                    return;
                }
                history.push(`${mappedInfoLink.url}?query=${email}`);
            } else {
                // history.push(`${mappedInfoLink.url}`);
                fields.bannernewtab ? window.open(mappedInfoLink.url, "_blank") : history.push(`${mappedInfoLink.url}`);
            }
        }
    };

    const onSubscribeClick = () => {
        setIsSubmitted(true);
        if (!validateEmail(email)) {
            return;
        }

        subscribe({
            email,
            onSuccess: () => {
                toasterContext.addToast({ body: siteMessage("Email_Subscribe_Success"), messageType: "success" });
            },
        });
    };

    const emailChangeHandler = (email: string) => {
        validateEmail(email);
        setEmail(email);
    };

    const validateEmail = (email: string) => {
        const errorMessage = !email ? emailRequiredFieldMessage : emailRegexp.test(email) ? "" : emailFieldMessage;
        setEmailError(errorMessage);
        return !errorMessage;
    };

    const gridItemCss = (gridItemProps?: GridItemProps) => {
        return css`
            ${gridItemProps?.css}
        `;
    };
    return (
        <StyledWrapper
            css={css`
                padding: 30px 0px;
                margin: 0 auto;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            css`
                                max-width: 100% !important;
                                padding: 27px 30px;
                            `,
                            css`
                                max-width: 100% !important;
                                padding: 27px 30px;
                            `,
                            css`
                                max-width: 720px !important;
                                padding: 30px 0px;
                            `,
                            css`
                                max-width: 960px !important;
                                padding: 30px 0px;
                            `,
                            css`
                                max-width: 1140px !important;
                                padding: 30px 0px;
                            `,
                        ],
                        "min",
                    )}
            `}
        >
            <StyledWrapper
                id="each-column"
                {...(fields.reverse ? styles.bannercontainerrevers : styles.bannercontainernormal)}
            >
                <StyledWrapper {...(fields.reverse ? styles.bannertextright : styles.bannertextleft)}>
                    {fields.title && <Typography {...styles.customFlyTitle}>{fields.title}</Typography>}
                    {fields.description && (
                        <Typography {...styles.customDescriptionText}>{fields.description}</Typography>
                    )}

                    {fields.bannerType && (
                        <StyledWrapper>
                            <GridItem {...styles.emailGridItem}>
                                {fields.bannerType === "news" && (
                                    <GridItem {...styles.emailGridIteminput}>
                                        <TextField
                                            {...styles.emailTextField}
                                            placeholder={fields.placeholder}
                                            value={email}
                                            onChange={e => emailChangeHandler(e.currentTarget.value)}
                                            error={isSubmitted && emailError}
                                        />
                                    </GridItem>
                                )}
                                <GridItem {...styles.buttonGrid}>
                                    {fields.bannerType === "news" ? (
                                        <Button
                                            {...(fields.bannerType === "news"
                                                ? styles.emailButton
                                                : styles.bannerButton)}
                                            onClick={() => onClick(history)}
                                            disabled={fields.bannerType === "news" && isSubmitted && !!emailError}
                                        >
                                            {fields.linkTitle}
                                        </Button>
                                    ) : (
                                        <Link
                                            {...styles.downloadLink}
                                            href={
                                                fields.linkType === "redirection"
                                                    ? mappedInfoLink?.url
                                                    : fields.uploadDocument
                                            }
                                            target={`${
                                                fields.bannernewtab || fields.linkType === "download" ? "_blank" : ""
                                            }`}
                                            download={fields.linkType === "download" && !fields.bannernewtab}
                                        >
                                            {fields.linkTitle}
                                        </Link>
                                    )}
                                </GridItem>
                            </GridItem>
                        </StyledWrapper>
                    )}
                </StyledWrapper>
                <StyledWrapper
                    css={css`
                        width: 47%;
                        ${({ theme }: { theme: BaseTheme }) =>
                            breakpointMediaQueries(
                                theme,
                                [
                                    css`
                                        width: 100%;
                                    `,
                                    css`
                                        width: 100%;
                                    `,
                                    css`
                                        width: 100%;
                                    `,
                                    css`
                                        width: 47%;
                                    `,
                                    css`
                                        width: 47%;
                                    `,
                                ],
                                "min",
                            )}
                    `}
                >
                    {fields.image && (
                        <StyledWrapper {...styles.customflyImgWrapper}>
                            <LazyImage src={fields.image} altText={fields.title} />
                        </StyledWrapper>
                    )}
                </StyledWrapper>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const smallBanner: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(INTbanner)),
    definition: {
        group: "Common",
        fieldDefinitions: [
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
                sortOrder: 0,
            },
            {
                name: fields.image,
                displayName: "Photo",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                fieldType: "General",
                sortOrder: 1,
            },
            {
                name: fields.description,
                displayName: "Description",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
                sortOrder: 2,
            },
            {
                name: fields.bannerType,
                displayName: "Banner Type",
                editorTemplate: "RadioButtonsField",
                defaultValue: "simple",
                fieldType: "Translatable",
                sortOrder: 3,
                options: [
                    {
                        displayName: "News Letter",
                        value: "news",
                    },
                    {
                        displayName: "Simple",
                        value: "simple",
                    },
                ],
            },
            {
                name: fields.linkType,
                displayName: "Link Type",
                editorTemplate: "RadioButtonsField",
                defaultValue: "redirection",
                fieldType: "Translatable",
                sortOrder: 4,
                options: [
                    {
                        displayName: "Download Link",
                        value: "download",
                    },
                    {
                        displayName: "Redirection Link",
                        value: "redirection",
                    },
                ],
            },
            {
                name: fields.uploadDocument,
                displayName: "Document Path",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
                sortOrder: 8,
                isVisible: (item?: HasFields) => {
                    return item?.fields.linkType === "download";
                },
            },
            {
                name: fields.placeholder,
                displayName: "Placeholder Text",
                editorTemplate: "TextField",
                defaultValue: "Enter email address",
                fieldType: "Translatable",
                sortOrder: 5,
                isVisible: (item?: HasFields) => {
                    return item?.fields.bannerType === "news";
                },
            },
            {
                name: fields.link,
                displayName: "Link",
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
                fieldType: "Translatable",
                allowUrls: item => true,
                allowCategories: item => false,
                sortOrder: 7,
                isVisible: (item?: HasFields) => {
                    return item?.fields.linkType === "redirection";
                },
            },
            {
                name: fields.bannernewtab,
                displayName: "Open Link In New Tab",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "Translatable",
                sortOrder: 9,
            },
            {
                name: fields.linkTitle,
                displayName: "Link Title",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
                sortOrder: 6,
            },
            {
                name: fields.reverse,
                editorTemplate: "CheckboxField",
                fieldType: "Translatable",
                defaultValue: false,
                sortOrder: 10,
            },
        ],
    },
};

export default smallBanner;
